import type { ReactNode } from "react";

/**
 * Small Markdown subset for blog posts edited in the admin. Renders React elements (no raw HTML),
 * so CMS text can never inject markup.
 *
 * Blocks: `## Heading`, `### Subheading`, `- item`, `1. item`, `> quote`, `![alt](url)`,
 * pipe tables (`| a | b |` + `| --- | --- |`), `@cta Title | Button label`, paragraphs.
 * Inline: `**bold**`, `*italic*`, `[text](url)`.
 */
export type MarkdownBlock =
  | { type: "heading"; level: 2 | 3; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "quote"; text: string }
  | { type: "image"; alt: string; src: string }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "cta"; title: string; label: string };

const splitRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

export function parseMarkdown(source: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const chunks = source.replace(/\r\n/g, "\n").split(/\n\s*\n/);
  for (const raw of chunks) {
    const chunk = raw.trim();
    if (!chunk) continue;
    const lines = chunk.split("\n");
    const first = lines[0];
    let match: RegExpMatchArray | null;
    if ((match = first.match(/^(#{2,6})\s+(.*)$/)) && lines.length === 1) {
      blocks.push({ type: "heading", level: match[1].length === 2 ? 2 : 3, text: match[2] });
    } else if (/^#\s+/.test(first) && lines.length === 1) {
      blocks.push({ type: "heading", level: 2, text: first.replace(/^#\s+/, "") });
    } else if (lines.every((line) => /^[-*]\s+/.test(line))) {
      blocks.push({ type: "list", ordered: false, items: lines.map((line) => line.replace(/^[-*]\s+/, "")) });
    } else if (lines.every((line) => /^\d+[.)]\s+/.test(line))) {
      blocks.push({ type: "list", ordered: true, items: lines.map((line) => line.replace(/^\d+[.)]\s+/, "")) });
    } else if (lines.every((line) => line.startsWith(">"))) {
      blocks.push({ type: "quote", text: lines.map((line) => line.replace(/^>\s?/, "")).join(" ") });
    } else if ((match = chunk.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/))) {
      blocks.push({ type: "image", alt: match[1], src: match[2] });
    } else if (lines.length >= 2 && lines.every((line) => line.trim().startsWith("|")) && /^\|?[\s:|-]+\|?$/.test(lines[1].trim())) {
      blocks.push({ type: "table", head: splitRow(lines[0]), rows: lines.slice(2).map(splitRow) });
    } else if ((match = chunk.match(/^@cta\s+(.*?)(?:\s*\|\s*(.*))?$/)) && lines.length === 1) {
      blocks.push({ type: "cta", title: match[1], label: match[2] || "" });
    } else {
      blocks.push({ type: "paragraph", text: lines.join(" ") });
    }
  }
  return blocks;
}

function safeHref(href: string) {
  return /^(https?:|mailto:|tel:|\/|#)/i.test(href) ? href : "#";
}

export function renderInline(text: string, keyPrefix = "i"): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let index = 0;
  while ((match = pattern.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${index++}`;
    if (match[1] !== undefined) {
      nodes.push(
        <strong key={key} className="font-bold text-white">
          {renderInline(match[1], key)}
        </strong>
      );
    } else if (match[2] !== undefined) {
      nodes.push(<em key={key}>{renderInline(match[2], key)}</em>);
    } else {
      const href = safeHref(match[4]);
      const external = /^https?:/i.test(href);
      nodes.push(
        <a
          key={key}
          href={href}
          className="text-[var(--public-accent)] underline underline-offset-2"
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {renderInline(match[3], key)}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
